// Mirrors AlertManager.Enums.CallScribe.CallScribeCallDirection (BE).
export enum CallScribeCallDirection {
  Outbound = 0,
  Inbound = 1,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeCallStatus (BE).
export enum CallScribeCallStatus {
  Initiated = 0,
  Ringing = 1,
  Answered = 2,
  Completed = 3,
  Failed = 4,
  NoAnswer = 5,
  Busy = 6,
  Cancelled = 7,
  Unknown = 8,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeSentiment (BE).
export enum CallScribeSentiment {
  Unknown = 0,
  Positive = 1,
  Neutral = 2,
  Negative = 3,
  Mixed = 4,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeCustomerEmotion (BE).
export enum CallScribeCustomerEmotion {
  Unknown = 0,
  Neutral = 1,
  Satisfied = 2,
  Confused = 3,
  Anxious = 4,
  Frustrated = 5,
  Angry = 6,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeCustomerOutcome (BE).
export enum CallScribeCustomerOutcome {
  Unknown = 0,
  Yes = 1,
  No = 2,
  Unclear = 3,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeJudgmentConfidence (BE).
export enum CallScribeJudgmentConfidence {
  Unknown = 0,
  Low = 1,
  Medium = 2,
  High = 3,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeSentimentEngine (BE).
export enum CallScribeSentimentEngine {
  SummaryPrompt = 0,
  GeminiAudioV2 = 1,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeSentimentGate (BE).
export enum CallScribeSentimentGate {
  NotRun = 0,
  Passed = 1,
  OptedOut = 2,
  NoRecording = 3,
  NoLabelledTranscript = 4,
  SpeakerSwapWindow = 5,
  InsufficientCustomerSpeech = 6,
  NoCustomerEvidence = 7,
  LowConfidence = 8,
  ValidationRejected = 9,
  ProviderFailed = 10,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeSentimentFeedbackVerdict (BE).
export enum CallScribeSentimentFeedbackVerdict {
  Up = 1,
  Down = 2,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeTimeEntryStatus (BE).
export enum CallScribeTimeEntryStatus {
  Suggested = 0,
  Posted = 1,
  Rejected = 2,
  Failed = 3,
  Claimed = 4,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribePipelineStage (BE).
export enum CallScribePipelineStage {
  Received = 0,
  Transcribing = 1,
  Summarizing = 2,
  Completed = 3,
  Failed = 4,
  NotApplicable = 5,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeConsentResult (BE).
export enum CallScribeConsentResult {
  Pending = 0,
  Granted = 1,
  Declined = 2,
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeSpeaker (BE).
export enum CallScribeSpeaker {
  Technician = 0,
  Customer = 1,
}

// GET /api/call-scribe/calls/access response.
export interface ICallScribeAccessInfo {
  hasAccess: boolean;
  reason: string | null;
  defaultFromNumber?: string | null;
  seesOnlyOwnCalls?: boolean;
  defaultCountryCode?: string | null;
  defaultDialCode?: string | null;
}

// GET /api/call-scribe/calls/voice-token response.
export interface ICallScribeVoiceToken {
  accessToken: string;
  identity: string;
  ttlSeconds: number;
}

export interface ICallScribeParticipant {
  id: number;
  role: CallScribeSpeaker | 'Technician' | 'Customer';
  label: string;
  nameSource: string | number;
  isPrimary: boolean;
  status?: CallScribeParticipantStatus | string | null;
  isMuted?: boolean | null;
}

export enum CallScribeParticipantStatus {
  Ringing = 0,
  Joined = 1,
  Left = 2,
  Declined = 3,
  NoAnswer = 4,
  Busy = 5,
  Failed = 6,
}

export interface ICallScribeTeammate {
  accountId: number;
  name: string;
  hasPhone: boolean;
}

export interface IAddCallParticipantRequest {
  kind: 'teammate' | 'contact' | 'number';
  accountId?: number;
  crmSourceId?: number;
  contactExternalId?: string;
  phone?: string;
}

// Projection from GET /api/call-scribe/calls/by-ticket.
export interface ICallScribeCallListItem {
  id: number;
  participants?: ICallScribeParticipant[];
  tenantId: number;
  crmSourceId: number | null;
  externalTicketId: string | null;
  companyExternalId: string | null;
  contactExternalId: string | null;
  initiatedByUserId: number | null;
  direction: CallScribeCallDirection;
  fromNumber: string | null;
  toNumber: string | null;
  status: CallScribeCallStatus;
  startedAtUtc: string | null;
  answeredAtUtc: string | null;
  endedAtUtc: string | null;
  durationSeconds: number | null;
  recordingDurationSeconds?: number | null;
  recordingTwilioUrl: string | null;
  recordingBlobPath: string | null;
  hasRecording: boolean;
  isCustomerRecordingOptIn: boolean;
  hasIntelligence: boolean;
  summaryShort: string | null;
  summaryCustomerFacing: string | null;
  summaryInternal: string | null;
  suggestedWorkTypeExternalId: string | null;
  suggestedDurationMinutes: number | null;
  postedDurationMinutes?: number | null;
  sentiment: CallScribeSentiment;
  sentimentScore: number | null;
  customerSentiment: CallScribeSentiment | null;
  customerDominantEmotion: CallScribeCustomerEmotion | null;
  customerEndingState: CallScribeCustomerEmotion | null;
  customerStartingState: CallScribeCustomerEmotion | null;
  customerPeakIntensity: number | null;
  customerOutcome: CallScribeCustomerOutcome | null;
  escalationRisk: number | null;
  escalationTriggers: string | null;
  sentimentConfidence: CallScribeJudgmentConfidence | null;
  evidenceQuote: string | null;
  technicianTone: CallScribeSentiment | null;
  sentimentEngine: CallScribeSentimentEngine;
  sentimentModel: string | null;
  sentimentGate: CallScribeSentimentGate | null;
  actionItems: string | null;
  topics: string | null;
  complianceFlags: string | null;
  linkStatus: CallScribeTimeEntryStatus | null;
  linkPostedAtUtc: string | null;
  linkErrorMessage: string | null;
  /**
   * Absent until the write-back half ships it, and absent must render no line at all rather than an
   * empty one: a call whose contact note was never attempted looks the same as one the API predates.
   */
  contactNote?: unknown;
  /**
   * SkippedByTechnician and TurnedOff are deliberately not Failed: a note nobody meant to post is
   * not an error, so the review reads these rather than inferring from a note's absence.
   */
  customerNoteOutcome?: string | null;
  internalNoteOutcome?: string | null;
  customerNoteDestination?: string | null;
  pipelineStage: CallScribePipelineStage | null;
  documentationUnavailableReason?: string | null;
  /**
   * Whether the server will post this call's time entry without anyone clicking. Populated only by
   * the single-call read, so it is undefined on list rows — and undefined must read as "show the
   * manual control", never as "the server declined". The rule itself lives on the server, beside
   * the trigger that acts on it; a second copy here would drift.
   */
  willPostAutomatically?: boolean | null;
  pipelineError: string | null;
  sipFailureReason: string | null;
  notRecordedReason?: string | null;
  notRecordedReasonText?: string | null;
  recordingStoppedAtUtc?: string | null;
  recordingStoppedByUserId?: number | null;
  isRecording?: boolean | null;
  recordingSid?: string | null;
  consentResult: CallScribeConsentResult | null;
  consentMode?: number | string | null;
  consentAtUtc: string | null;
  createdAtUtc: string;
  /**
   * Display name of the PSA contact this call relates to, resolved server-side from
   * `contactExternalId` (preferred) or by reverse-matching `fromNumber`/`toNumber`
   * against the ticket's contact phones. Null when no PSA contact can be resolved —
   * Pod cards fall back to the formatted phone number.
   */
  contactName: string | null;
}

// One row of GET /api/call-scribe/calls/{callId}/empathic-analyses.
export interface ICallScribeCallEmotion {
  id: number;
  provider: string;
  customerDominantEmotion: string | null;
  customerEmotionsJson: string | null;
  customerEmotionalArcJson: string | null;
  agentDominantEmotion: string | null;
  agentEmotionsJson: string | null;
  agentEmotionalArcJson: string | null;
  emotionalAlignmentScore: number | null;
  providerLatencyMs: number | null;
  model: string | null;
  processedAtUtc: string;
  /** Sentiment v2 only: the schema-shaped judgment document. Null for every other provider. */
  judgmentJson: string | null;
}

// The raw shape of a parsed `judgmentJson` document (schemaVersion 2). Every field is
// unknown/loosely typed on purpose: parseSentimentJudgment is the one place that turns this
// into a trusted ISentimentJudgment, refusing (null) on anything it doesn't recognise.
export interface ISentimentJudgmentDocument {
  schemaVersion?: unknown;
  channelMode?: unknown;
  audioOffsetMs?: unknown;
  turns?: unknown;
  customerMinutes?: unknown;
  customerState?: unknown;
  technicianState?: unknown;
  validation?: unknown;
  gate?: unknown;
}

// Wire values are CallScribeCustomerEmotion's C# member names, "Unknown" excluded in practice
// (a turn or minute is only emitted for a customer state the model actually judged).
export type SentimentJudgmentEmotionName =
  | 'Neutral'
  | 'Satisfied'
  | 'Confused'
  | 'Anxious'
  | 'Frustrated'
  | 'Angry';

// Wire values are CallScribeJudgmentConfidence's C# member names.
export type SentimentJudgmentConfidenceName = 'Low' | 'Medium' | 'High';

// Wire values are CallScribeCustomerOutcome's C# member names.
export type SentimentJudgmentOutcomeName = 'Yes' | 'No' | 'Unclear';

export type SentimentJudgmentSpeakerName = 'customer' | 'technician';

export type SentimentJudgmentTrigger =
  | 'asks_for_manager'
  | 'threatens_cancel'
  | 'repeat_contact'
  | 'blames_msp'
  | 'unresolved_at_end';

export interface ISentimentJudgmentTurn {
  turnId: string;
  speaker: SentimentJudgmentSpeakerName;
  startMs: number;
  endMs: number;
  emotion: SentimentJudgmentEmotionName;
  intensity: number;
  cues: string[];
  quote: string;
  confidence: SentimentJudgmentConfidenceName;
  isEvidence: boolean;
}

export interface ISentimentJudgmentMinute {
  minute: number;
  emotion: SentimentJudgmentEmotionName;
  intensity: number;
}

export interface ISentimentJudgmentTriggerEvidence {
  trigger: SentimentJudgmentTrigger;
  turnIds: string[];
}

export interface ISentimentJudgmentCustomerState {
  reportedProblem: boolean;
  satisfiedWithOutcome: SentimentJudgmentOutcomeName;
  triggers: ISentimentJudgmentTriggerEvidence[];
  /** The model's own, uncapped risk read. `escalationRisk` is this after D12's cap table. */
  escalationRiskModel: number;
  escalationRisk: number;
  confidence: SentimentJudgmentConfidenceName;
}

export interface ISentimentJudgmentTechnicianState {
  interruptions: number;
  deEscalationAttempted: boolean;
  empathy: number;
}

export interface ISentimentJudgmentValidation {
  status: 'accepted' | 'rejected';
  attempts: number;
  quoteVerificationRate: number;
  reasons: string[];
}

// Wire values are CallScribeSentimentGate's C# member names (e.g. "Passed", "LowConfidence").
export interface ISentimentJudgmentGate {
  result: string;
  customerWords: number;
  customerTalkSec: number;
}

/**
 * The trusted, parsed shape of a sentimentEngine=GeminiAudioV2 call's judgmentJson.
 * customerState/technicianState are both null on a rejected judgment (empty turns);
 * validation.status and gate.result say why.
 */
export interface ISentimentJudgment {
  channelMode: 'dual' | 'mono';
  audioOffsetMs: number;
  turns: ISentimentJudgmentTurn[];
  customerMinutes: ISentimentJudgmentMinute[];
  customerState: ISentimentJudgmentCustomerState | null;
  technicianState: ISentimentJudgmentTechnicianState | null;
  validation: ISentimentJudgmentValidation;
  gate: ISentimentJudgmentGate;
}

// Body for PUT /api/call-scribe/calls/{callId}/sentiment-feedback.
export interface IUpsertSentimentFeedbackRequest {
  verdict: CallScribeSentimentFeedbackVerdict;
  correctedCustomerSentiment?: CallScribeSentiment | null;
  correctedOutcome?: CallScribeCustomerOutcome | null;
  correctedEscalationRisk?: number | null;
  comment?: string | null;
}

// GET/PUT response for /api/call-scribe/calls/{callId}/sentiment-feedback.
export interface ISentimentFeedback {
  id: number;
  callScribeCallId: number;
  verdict: CallScribeSentimentFeedbackVerdict;
  correctedCustomerSentiment: CallScribeSentiment | null;
  correctedOutcome: CallScribeCustomerOutcome | null;
  correctedEscalationRisk: number | null;
  comment: string | null;
  judgedCustomerSentiment: CallScribeSentiment | null;
  judgedEscalationRisk: number | null;
  judgedConfidence: CallScribeJudgmentConfidence | null;
  judgedEngine: CallScribeSentimentEngine;
  judgedModel: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

// Body for POST /api/call-scribe/calls.
export interface IInitiateCallRequest {
  fromNumber: string;
  toNumber: string;
  crmSourceId?: number | null;
  externalTicketId?: string | null;
  companyExternalId?: string | null;
  contactExternalId?: string | null;
}

export interface ICallScribeTimeEntryOption {
  value: string;
  label: string;
  isDefault: boolean;
}

export interface ICallScribeNoteDestination {
  value: string;
  label: string;
  isCustomerVisible: boolean;
}

/**
 * Always sent, never null, with both flags false when nothing is allowed - a null would make "not
 * allowed" and "an older server" the same reading. Controls gate on the FLAGS, never on this object
 * being present, and customerNoteDestinations is empty whenever allowDestinationOverride is false.
 */
export interface ICallScribeNoteOverrides {
  allowDestinationOverride: boolean;
  allowInternalNoteEdit: boolean;
  customerNoteDestinations: ICallScribeNoteDestination[];
  defaultCustomerNoteDestination?: string | null;
  /** Null on CW/Halo "No text"; the V1 render otherwise (the neutral line on Autotask). */
  timeEntryTextPrefill?: string | null;
  /** False when the tenant row is off on CW/Halo, so the field stays read-only for this call. */
  timeEntryTextEditable?: boolean;
  /** The rendered customer-note template, or the customer-facing summary when none is enabled. */
  customerNotePrefill?: string | null;
  customerNoteEnabled?: boolean | null;
}

export interface ICallScribeTimeEntryOptions {
  supported: boolean;
  unsupportedReason?: string | null;
  workTypesSupported?: boolean;
  workTypesUnsupportedReason?: string | null;
  billingTypesSupported?: boolean;
  billingTypesUnsupportedReason?: string | null;
  workTypes: ICallScribeTimeEntryOption[];
  billingTypes: ICallScribeTimeEntryOption[];
  noteOverrides?: ICallScribeNoteOverrides;
}

// Body for POST /api/call-scribe/calls/{callId}/time-entry.
export interface ICallScribeTimeEntryRequest {
  durationMinutes: number;
  externalTicketId?: string | null;
  workTypeExternalId?: string | null;
  customerNotes?: string | null;
  internalNotes?: string | null;
  billable?: boolean | null;
  billingTypeExternalId?: string | null;
  /**
   * Omitted entirely when unchanged. A null destination means "use the tenant's setting"; the
   * server refuses a destination the tenant has not allowed rather than posting elsewhere.
   */
  customerNoteDestination?: string | null;
  skipCustomerNote?: boolean | null;
  skipInternalNote?: boolean | null;
  /** Omitted entirely by an old pod, which gets the tenant setting, byte-identical to today. */
  timeEntryText?: string | null;
  /** True: CW/Halo send no text, Autotask sends the neutral constant. */
  skipTimeEntryText?: boolean | null;
}

export enum CallScribeNoteOutcome {
  NotRecorded = 'NotRecorded',
  Posted = 'Posted',
  Failed = 'Failed',
  SkippedByTechnician = 'SkippedByTechnician',
  TurnedOff = 'TurnedOff',
  /**
   * The note rendered to nothing - voicemail, an inaudible call, a failed summary, or a template
   * whose tokens all came out empty - so it was not posted. Distinct from TurnedOff (the tenant's
   * setting) and SkippedByTechnician (a per-call choice): nobody decided against it, there was
   * simply nothing to say.
   */
  SkippedEmpty = 'SkippedEmpty',
}

// Result row returned by /time-entry and /reject endpoints.
export interface ICallScribeTimeEntryLink {
  id: number;
  status: CallScribeTimeEntryStatus;
  postedAtUtc?: string | null;
  errorMessage?: string | null;
}

// Mirrors AlertManager.Enums.CallScribe.CallScribeTranscriptProvider (BE).
export enum CallScribeTranscriptProvider {
  OpenAiWhisper = 0,
}

// GET /api/call-scribe/calls/{callId}/transcript-segments response row.
export interface ICallScribeTranscriptSegment {
  id: number;
  callScribeCallId: number;
  seq: number;
  speaker: CallScribeSpeaker;
  participantId?: number | null;
  text: string;
  startMs: number | null;
  endMs: number | null;
  createdAtUtc: string;
}

// Normalized live segment shape shared by the REST catch-up and the ws event stream.
export interface ILiveTranscriptSegment {
  seq: number;
  speaker: CallScribeSpeaker;
  participantId?: number | null;
  text: string;
  startMs: number | null;
  endMs: number | null;
  committed: boolean;
}

// GET /api/call-scribe/calls/{callId}/transcript response.
export interface ICallScribeCallTranscript {
  id: number;
  callScribeCallId: number;
  provider: CallScribeTranscriptProvider;
  providerModel: string | null;
  rawText: string | null;
  redactedText: string | null;
  language: string | null;
  wordCount: number | null;
  durationSeconds: number | null;
  completedAtUtc: string | null;
  errorMessage: string | null;
}
